import useClientState from 'composables/client-state';

const { getSessionStorage, setSessionStorage, removeSessionStorage } = useClientState();

export default {
  namespaced: true,

  state() {
    return {
      enrollmentRules: [],
      initialRulesSet: false,
      hrisCategories: [],
      atsCategories: [],
      autoSaveTimeout: null,
    };
  },

  getters: {
    getRuleByIndex: (state) => (idx) => state.enrollmentRules[idx],
    getAllRules: (state) => state.enrollmentRules,
    initialRulesSet: (state) => state.initialRulesSet,
    getCategoryLabelAndName: (state) => (ruleIdx, categoryType, categoryValue) => {
      const rule = state.enrollmentRules[ruleIdx];
      if (!rule || !categoryType || !categoryValue) return [categoryType || '', categoryValue || ''];

      // Determine which categories array to use based on integration_identity_type
      const categories = rule.integration_identity_type === 'HrisIdentity'
        ? state.hrisCategories
        : state.atsCategories;

      // Set default values. These will be used if no match is found.
      let categoryLabel = categoryType;
      let optionName = categoryValue;

      for (const cat of categories) {
        for (const opt of cat.options || []) {
          if ((cat.hris_type === categoryType || cat.key === categoryType) && opt.hris_value === categoryValue) {
            optionName = opt.name;
            categoryLabel = cat.label;
            break;
          }
        }

        if (categoryLabel !== categoryType) break; // If we found a match, no need to continue
      }

      return [categoryLabel, optionName];
    },
    getFormula: (state) => (ruleIdx, groupIdx, formulaIdx) => {
      const rule = state.enrollmentRules[ruleIdx];
      if (!rule || !rule.processing_rule || !rule.processing_rule.groups) return null;

      const group = rule.processing_rule.groups[groupIdx];
      if (!group || !group.formulas) return null;

      return group.formulas[formulaIdx] || null;
    },
    hasDraftForRule: (state, getters, rootState) => (ruleIdx, sourceType) => {
      const teamId = rootState.currentTeam?.id;
      if (!teamId || ruleIdx < 0) return false;

      try {
        const draftKey = `enrollment_rule_drafts_${teamId}`;
        const drafts = getSessionStorage(draftKey);
        if (!drafts) return false;

        const parsedDrafts = JSON.parse(drafts);
        return Boolean(parsedDrafts[ruleIdx]?.[sourceType]);
      } catch (e) {
        // Handle sessionStorage access errors, JSON parsing errors
        return false;
      }
    },
  },

  mutations: {
    update(state, { key, value }) {
      state[key] = value;
    },

    updateRuleValue(state, { idx, key, value }) {
      state.enrollmentRules[idx][key] = value;
    },

    addNewRule(state, { teamId, integrationIdentityId, integrationIdentityType }) {
      state.enrollmentRules[state.enrollmentRules.length] = {
        team_id: teamId,
        integration_identity_type: integrationIdentityType,
        integration_identity_id: integrationIdentityId,
        tag_ids: [],
        processing_rule: {
          groups: [],
          version: 1,
        },
      };
    },

    deleteRuleAtIdx(state, { idx }) {
      const firstHalf = state.enrollmentRules.slice(0, idx);
      const secondHalf = state.enrollmentRules.slice(idx + 1, state.enrollmentRules.length);
      state.enrollmentRules = firstHalf.concat(secondHalf);
    },

    deleteFormulaAtIdx(state, { ruleIdx, groupIdx, formulaIdx }) {
      const rule = state.enrollmentRules[ruleIdx];
      if (!rule || !rule.processing_rule || !rule.processing_rule.groups) return;

      const group = rule.processing_rule.groups[groupIdx];
      if (!group || !group.formulas) return;

      // Remove the formula at the specified index
      const firstHalf = group.formulas.slice(0, formulaIdx);
      const secondHalf = group.formulas.slice(formulaIdx + 1, group.formulas.length);
      group.formulas = firstHalf.concat(secondHalf);

      // If the group has no formulas left, remove the group entirely
      if (group.formulas.length === 0) {
        const firstHalfGroups = rule.processing_rule.groups.slice(0, groupIdx);
        const secondHalfGroups = rule.processing_rule.groups.slice(groupIdx + 1, rule.processing_rule.groups.length);
        rule.processing_rule.groups = firstHalfGroups.concat(secondHalfGroups);
      }
    },

    clearRuleAtIdx(state, { ruleIdx }) {
      const rule = state.enrollmentRules[ruleIdx];
      if (!rule) return;

      // Reset the rule to empty groups
      rule.processing_rule = {
        groups: [],
        version: 1,
      };
    },

    saveFormula(state, {
      ruleIdx, groupIdx, formulaIdx, type, operator, value,
    }) {
      const rule = state.enrollmentRules[ruleIdx];
      if (!rule) return;

      // Ensure processing_rule exists
      if (!rule.processing_rule) {
        rule.processing_rule = { groups: [], version: 1 };
      }

      // Ensure groups array exists and has the group at groupIdx
      if (!rule.processing_rule.groups) {
        rule.processing_rule.groups = [];
      }

      // Ensure the group exists at groupIdx
      if (!rule.processing_rule.groups[groupIdx]) {
        rule.processing_rule.groups[groupIdx] = { formulas: [] };
      }

      const group = rule.processing_rule.groups[groupIdx];

      // Ensure formulas array exists
      if (!group.formulas) {
        group.formulas = [];
      }

      // Create or update the formula at formulaIdx
      const formula = {
        type,
        operator,
        value,
      };

      group.formulas[formulaIdx] = formula;
    },

    // Draft management mutations
    saveDraftRule(state, { teamId, ruleIdx, sourceType, ruleData }) {
      if (!teamId || ruleIdx < 0 || !sourceType || !ruleData) return;

      const draftKey = `enrollment_rule_drafts_${teamId}`;
      let drafts = {};

      try {
        const existingDrafts = getSessionStorage(draftKey);
        if (existingDrafts) {
          drafts = JSON.parse(existingDrafts);
        }
      } catch (e) {
        // If parsing fails, start with empty drafts object
        drafts = {};
      }

      // Ensure the rule index exists in drafts
      if (!drafts[ruleIdx]) {
        drafts[ruleIdx] = {};
      }

      // Save the draft data for this rule and source type
      drafts[ruleIdx][sourceType] = {
        tag_ids: ruleData.tag_ids || [],
        processing_rule: ruleData.processing_rule || { groups: [], version: 1 },
        lastUpdated: Date.now(),
      };

      try {
        setSessionStorage(draftKey, JSON.stringify(drafts));
      } catch (e) {
        // Handle sessionStorage quota exceeded or access errors
      }
    },

    restoreRuleFromDraft(state, { teamId, ruleIdx, sourceType }) {
      if (!teamId || ruleIdx < 0 || !sourceType || !state.enrollmentRules[ruleIdx]) return;

      const draftKey = `enrollment_rule_drafts_${teamId}`;

      try {
        const drafts = getSessionStorage(draftKey);
        if (!drafts) return;

        const parsedDrafts = JSON.parse(drafts);
        const draftData = parsedDrafts[ruleIdx]?.[sourceType];

        if (draftData) {
          state.enrollmentRules[ruleIdx].tag_ids = draftData.tag_ids || [];
          state.enrollmentRules[ruleIdx].processing_rule = draftData.processing_rule || { groups: [], version: 1 };
        }
      } catch (e) {
        // Handle sessionStorage access errors, JSON parsing errors
      }
    },

    clearAllDrafts(state, { teamId }) {
      const draftKey = `enrollment_rule_drafts_${teamId}`;
      removeSessionStorage(draftKey);
    },

    setAutoSaveTimeout(state, timeoutId) {
      if (state.autoSaveTimeout) {
        clearTimeout(state.autoSaveTimeout);
      }
      state.autoSaveTimeout = timeoutId;
    },

    clearAutoSaveTimeout(state) {
      if (state.autoSaveTimeout) {
        clearTimeout(state.autoSaveTimeout);
        state.autoSaveTimeout = null;
      }
    },
  },

  actions: {
    establishInitialState({ commit }, { enrollmentRules, hrisCategories, atsCategories }) {
      commit('update', { key: 'enrollmentRules', value: enrollmentRules });
      commit('update', { key: 'initialRulesSet', value: enrollmentRules.length > 0 });
      commit('update', { key: 'hrisCategories', value: hrisCategories });
      commit('update', { key: 'atsCategories', value: atsCategories });
    },

    saveDraftForRule({ commit, rootState }, { ruleIdx, sourceType }) {
      const rule = rootState.enrollmentRules.enrollmentRules[ruleIdx];
      if (!rule) return;

      const teamId = rootState.currentTeam?.id;
      if (!teamId) return;

      commit('saveDraftRule', {
        teamId,
        ruleIdx,
        sourceType,
        ruleData: {
          tag_ids: rule.tag_ids,
          processing_rule: rule.processing_rule,
        },
      });
    },

    restoreRuleFromDraft({ commit, rootState }, { ruleIdx, sourceType }) {
      const teamId = rootState.currentTeam?.id;
      if (!teamId) return;

      commit('restoreRuleFromDraft', { teamId, ruleIdx, sourceType });
    },

    clearAllRuleDrafts({ commit, rootState }) {
      const teamId = rootState.currentTeam?.id;
      if (!teamId) return;

      commit('clearAllDrafts', { teamId });
    },

    autoSaveRule({ commit, dispatch, state }, { ruleIdx }) {
      const rule = state.enrollmentRules[ruleIdx];
      if (!rule || !rule.integration_identity_type) return;

      const sourceType = rule.integration_identity_type === 'HrisIdentity' ? 'hris' : 'ats';

      // Clear existing timeout to prevent memory leaks
      commit('clearAutoSaveTimeout');

      // Debounce auto-save to avoid excessive localStorage writes
      const timeoutId = setTimeout(() => {
        dispatch('saveDraftForRule', { ruleIdx, sourceType });
      }, 500);

      commit('setAutoSaveTimeout', timeoutId);
    },
    openRulesFilterModal({ commit, state }, { ruleIdx, groupIdx, formulaIdx }) {
      const rule = state.enrollmentRules[ruleIdx];
      if (!rule) return;

      let groupIdxToUse = groupIdx;
      if (groupIdxToUse === null || groupIdxToUse === undefined) { // If no groupIdx is provided, use the last group
        groupIdxToUse = rule.processing_rule.groups.length;
      }

      let formulaIdxToUse = formulaIdx;
      if (formulaIdxToUse === null || formulaIdxToUse === undefined) { // If no formulaIdx is provided, use the first formula
        formulaIdxToUse = 0;
      }

      commit('updateModal', {
        visible: true,
        subcomponent: 'onboarding-tags-enrollment-modals-rules-filter',
        size: 'xl of-v',
        data: {
          integrationIdentityType: rule.integration_identity_type,
          ruleIdx,
          groupIdx: groupIdxToUse,
          formulaIdx: formulaIdxToUse,
        },
      }, { root: true });
    },
  },
};
